import type React from "react";
import { useState, useRef, useEffect } from "react";
import Markdown from "markdown-to-jsx";
import Card from "../Card";
import logo from "../../assets/logo.svg";
import { ModelSelector } from "./ModelSelector";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Send, SendIcon } from "lucide-react";
import { Badge } from "../ui/badge";
import { Skeleton } from "../ui/skeleton";
import hljs from "highlight.js";
import "highlight.js/styles/github.css";
// import "highlight.js/styles/github-dark.css";
import typescript from "highlight.js/lib/languages/typescript";
import type { ModelName } from "@butler/shared";
import { cn } from "@/lib/utils";

hljs.registerLanguage("typescript", typescript);

interface Message {
  role: "user" | "assistant";
  content: string;
}

interface ChatWindowProps {
  title: string;
  onSubmit: (message: string) => Promise<void>;
  messages: Message[];
  variant: "small" | "large";
  isStreaming: boolean;
  selectedModel?: ModelName;
  onModelChange?: (model: ModelName) => void;
}

export const ChatWindow: React.FC<ChatWindowProps> = ({
  title,
  variant = "large",
  onSubmit,
  messages,
  isStreaming,
  selectedModel,
  onModelChange,
}) => {
  const [input, setInput] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      // textareaRef.current.style.height = "auto";
      // textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isStreaming) return;

    try {
      // Send message to parent component
      void onSubmit(input);
      setInput("");
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const EmptyState = () => {
    return (
      <div className="h-full flex flex-col justify-center items-center text-center py-20 ">
        <img
          src={logo}
          alt="logo"
          className="w-40 h-40 mx-auto mb-4 opacity-70"
        />
        <p>No messages yet. Start by describing your agent.</p>
        <p className="mt-2 text-sm">
          Example: "I need an agent that can monitor my email and summarize
          important messages."
        </p>
        <DefaultPills
          onSelect={async (input) => {
            setInput(input);
            void onSubmit(input);
            setInput("");
          }}
        />
      </div>
    );
  };

  return (
    <Card
      title={title}
      className={`h-full w-full flex flex-col text-primary ${
        variant === "large" ? "mix-w-2xl" : "min-w-xl"
      }`}
    >
      <div className="flex-1 w-full overflow-y-auto mb-4">
        {messages.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="space-y-4 flex flex-col gap-4">
            {messages.map((message, index) => (
              <div
                key={message.content}
                className={`w-3/4 p-3 rounded-lg ${
                  message.role === "user"
                    ? "bg-secondary ml-4 self-end"
                    : "bg-accent mr-4"
                }`}
              >
                <div className="font-medium mb-1 text-sm">
                  {message.role === "user" ? "You" : "Assistant"}
                </div>
                <div className="text-xl tracking-wide leading-relaxed">
                  <RenderMarkdown content={message.content} />
                </div>
              </div>
            ))}
            {isStreaming && (
              <div className="flex flex-col gap-2">
                <Skeleton className="w-2/3 h-10" />
                <Skeleton className="w-2/3 h-10" />
                <Skeleton className="w-2/3 h-10" />
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="mt-auto">
        <div className="relative">
          <Textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            className="min-h-24"
            placeholder="Type your message..."
            disabled={isStreaming}
          />
          <div className="flex absolute gap-2 right-2 bottom-2">
            <ModelSelector
              selectedModel={selectedModel}
              onModelChange={onModelChange}
              disabled={isStreaming}
            />
            <Button type="submit" disabled={!input.trim() || isStreaming}>
              <Send />
            </Button>
          </div>
        </div>
      </form>
    </Card>
  );
};

export const DefaultPills = ({
  pills = [
    "What can you do?",
    "What tools do you have?",
    "How you can help me",
  ],
  onSelect,
  layout = "horizontal",
}: {
  pills?: string[];
  layout?: "horizontal" | "vertical";
  onSelect: (input: string) => void;
}) => {
  return (
    <div
      className={cn(
        "flex flex-wrap gap-2 w-full mx-auto justify-center my-10",
        layout === "vertical" && "flex-col items-center",
      )}
    >
      {pills.map((pill) => (
        <Button
          type="button"
          variant="outline"
          className="block text-left bg-accent text-xs h-10 overflow-hidden scroll-auto truncate  border border-border rounded-lg mx-auto max-w-64"
          onClick={() => {
            onSelect(pill);
          }}
          key={pill}
        >
          {pill}
        </Button>
      ))}
    </div>
  );
};

function SyntaxHighlightedCode(props: React.HTMLProps<HTMLPreElement>) {
  const ref = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (ref.current && props.className?.includes("lang-")) {
      // hljs.registerLanguage('typescript', typescript);
      hljs.highlightElement(ref.current);

      // hljs won't reprocess the element unless this attribute is removed
      ref.current.removeAttribute("data-highlighted");
    }
  }, [props.className]);

  // check the ref if it's a single or multi line code
  const isMultiLine = ref.current?.textContent?.includes("\n");
  if (isMultiLine) {
    return (
      <div className="overflow-x-auto rounded-md border-2 border-color-primary my-2 hover:shadow-sm">
        <code {...props} ref={ref} className="bg-card rounded " />
      </div>
    );
  }
  return <code {...props} ref={ref} />;
}

export function RenderMarkdown({ content }: { content: string }) {
  return (
    <Markdown
      // biome-ignore lint/correctness/noChildrenProp: <explanation>
      children={content}
      options={{
        overrides: {
          code: SyntaxHighlightedCode,
        },
      }}
    />
  );
}

export default ChatWindow;
